import React, { createContext, useContext, useState, ReactNode } from "react";
import { Agent } from "../../model_gen/agent_base";

export interface Message {
  id: string;
  content: string;
  timestamp: number;
  mentions: Agent[];
  role?: "user" | "assistant";
  appliedChanges?: any[]; // Store the changes that were applied for potential revert
  isSystemMessage?: boolean; // Flag for internal messages that shouldn't be displayed in chat
  suggestions?: any; // Store the pending suggestions (proposed_changes, action, etc.)
  suggestionsStatus?: "pending" | "approved" | "rejected" | "partial" | "ignored"; // Status of suggestions
  suggestionsExpanded?: boolean; // Whether the processed suggestions are expanded
}

interface AssistantContextType {
  messages: Message[];
  addMessage: (content: string, mentions: Agent[], role?: "user" | "assistant", appliedChanges?: any[], isSystemMessage?: boolean, suggestions?: any) => void;
  clearMessages: () => void;
  updateMessageSuggestions: (messageId: string, status: "approved" | "rejected" | "partial" | "ignored", appliedChanges?: any[]) => void;
  toggleMessageExpanded: (messageId: string) => void;
}

const AssistantContext = createContext<AssistantContextType | undefined>(undefined);

export const useAssistant = () => {
  const context = useContext(AssistantContext);
  if (!context) {
    throw new Error("useAssistant must be used within an AssistantProvider");
  }
  return context;
};

interface AssistantProviderProps {
  children: ReactNode;
}

export const AssistantProvider: React.FC<AssistantProviderProps> = ({ children }) => {
  const [messages, setMessages] = useState<Message[]>([]);

  const addMessage = (content: string, mentions: Agent[], role: "user" | "assistant" = "user", appliedChanges?: any[], isSystemMessage?: boolean, suggestions?: any) => {
    const newMessage: Message = {
      id: crypto.randomUUID(),
      content,
      timestamp: Date.now(),
      mentions,
      role,
      appliedChanges,
      isSystemMessage,
      suggestions,
      suggestionsStatus: suggestions ? "pending" : undefined,
      suggestionsExpanded: false
    };
    setMessages((prev) => [...prev, newMessage]);
  };

  const clearMessages = () => {
    setMessages([]);
  };

  const updateMessageSuggestions = (messageId: string, status: "approved" | "rejected" | "partial" | "ignored", appliedChanges?: any[]) => {
    setMessages(prev => prev.map(msg => 
      msg.id === messageId 
        ? { ...msg, suggestionsStatus: status, appliedChanges: appliedChanges || msg.appliedChanges }
        : msg
    ));
  };

  const toggleMessageExpanded = (messageId: string) => {
    setMessages(prev => prev.map(msg => 
      msg.id === messageId 
        ? { ...msg, suggestionsExpanded: !msg.suggestionsExpanded }
        : msg
    ));
  };

  return (
    <AssistantContext.Provider value={{ messages, addMessage, clearMessages, updateMessageSuggestions, toggleMessageExpanded }}>
      {children}
    </AssistantContext.Provider>
  );
};
